'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, Suspense } from 'react';
import { usePathname } from 'next/navigation';
import { getFavoritesCount } from '@/lib/favorites';
import { isReviewHubPath } from '@/lib/routes/review-hub';
import { companyConfig } from '@/lib/config-loader';
import ApartmentSearchBar from '@/components/apartments/ApartmentSearchBar';
import SmartWhatsAppCTA from '@/components/whatsapp/SmartWhatsAppCTA';
import WhatsAppGlyph from '@/components/whatsapp/WhatsAppGlyph';
import { englishGuideDestination } from '@/lib/seo/guide-languages';

export default function Header() {
  const pathname = usePathname();
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  // Admin pages use their own layout chrome — don't render the public header.
  const isAdminPage = pathname?.startsWith('/admin');
  const isOperatorFlyerPage =
    pathname === '/cholon-tour/flyer' ||
    /^\/tours\/[^/]+\/flyer$/.test(pathname ?? '');

  // Check if we're on an apartment detail page
  const isApartmentPage = pathname?.startsWith('/apartamentos/') && pathname !== '/apartamentos';

  // Check if we're on the availability/results page
  const isAvailabilityPage = pathname === '/reservas';
  const isContactPage = pathname === '/contacto';

  // Tours pages: fondo blanco sin gradient oscuro, header con shadow sutil
  const isToursPage = pathname === '/tours' || pathname?.startsWith('/tours/');
  const isBlogPage = pathname === '/blog' || pathname?.startsWith('/blog/');
  const isBuildingPage = pathname?.startsWith('/edificios/');

  // Determine header background based on current page
  // Apartment page: efecto "encoger al scroll" estilo luisdelmar (shadow + blur dinámico)
  const headerBg = isApartmentPage
    ? isScrolled
      ? 'bg-white/95 backdrop-blur-lg shadow-lg shadow-gray-200/50'
      : 'bg-white shadow-sm'
    : isBuildingPage
    ? isScrolled
      ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-100'
      : 'bg-gradient-to-b from-black/35 to-transparent'
    : isAvailabilityPage || isContactPage
    ? 'bg-white shadow-sm'
    : isBlogPage
    ? 'bg-white/95 backdrop-blur-md shadow-sm'
    : isToursPage
    ? 'bg-white/80 backdrop-blur-md shadow-sm'
    : 'bg-gradient-to-b from-black/35 to-transparent';

  // Tema claro (logo oscuro, pills oscuras) cuando el fondo es blanco
  const isLightHeader =
    isApartmentPage || isToursPage || isBlogPage || isAvailabilityPage || isContactPage || (isBuildingPage && isScrolled);
  const headerPosition = isBuildingPage ? 'absolute' : 'fixed';

  useEffect(() => {
    const updateFavoritesCount = () => {
      setFavoritesCount(getFavoritesCount());
    };

    window.addEventListener('storage', updateFavoritesCount);
    updateFavoritesCount();

    return () => {
      window.removeEventListener('storage', updateFavoritesCount);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hubs de reseñas públicos y de operación: herramienta enfocada, sin header público.
  if (isAdminPage || isOperatorFlyerPage || isReviewHubPath(pathname) || /^\/(en|pt)\//.test(pathname ?? '')) return null;

  return (
    // Header fijo transparente siempre - más compacto en páginas de apartamentos
    <header className={`${headerPosition} top-0 left-0 right-0 z-50 w-full ${headerBg} transition-all duration-500 ease-out`}>
      {/* Borde inferior animado con shimmer — solo en página de apartamento (estilo luisdelmar) */}
      {isApartmentPage && (
        <div
          className={`
            absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-primary-300 via-primary-500 to-primary-300
            bg-[length:200%_auto] animate-shimmer transition-opacity duration-500 pointer-events-none
            ${isScrolled ? 'opacity-80' : 'opacity-50'}
          `}
          aria-hidden="true"
        />
      )}
      <nav className={`container mx-auto max-w-7xl 2xl:max-w-[1600px] 3xl:max-w-[1800px] px-4 sm:px-6 lg:px-8 2xl:px-12 transition-all duration-500 ease-out ${isApartmentPage ? (isScrolled ? 'py-1' : 'pt-2 pb-1.5') : 'pt-3 pb-2'}`}>
        <div className="flex items-center justify-between gap-2 min-[400px]:gap-4">
          {/* Logo — isotipo solo en detail de apartamento, isotipo+wordmark en el resto */}
          <Link
            href="/"
            className="flex min-w-0 items-center gap-2 relative group"
            aria-label={companyConfig.brand.name}
          >
            <Image
              src="/logo-header-star.png"
              alt={isApartmentPage ? companyConfig.brand.name : ''}
              width={48}
              height={48}
              priority
              className={`flex-shrink-0 transition-all duration-500 ease-out group-hover:scale-105 ${
                isApartmentPage
                  ? isScrolled
                    ? 'w-7 h-7 lg:w-8 lg:h-8'
                    : 'w-9 h-9 lg:w-10 lg:h-10'
                  : 'w-9 h-9 lg:w-10 lg:h-10'
              }`}
            />
            {!isApartmentPage && !isAvailabilityPage && (
              <span
                style={{ fontFamily: 'var(--font-fraunces)', letterSpacing: '-0.03em' }}
                className={`min-w-0 truncate font-bold transition-all duration-300 group-hover:opacity-80 text-lg min-[400px]:text-2xl ${
                  isLightHeader ? 'text-gray-900' : 'text-white'
                }`}
              >
                Pa&apos;Cartagena
              </span>
            )}
          </Link>

          {/* Pildora de contexto de búsqueda — solo en detail de apartamento.
              Muestra fechas/noches/personas si hay búsqueda activa, fallback a "Buscar". */}
          {isApartmentPage && (
            <div className="flex flex-1 justify-center min-w-0">
              <Suspense fallback={null}>
                <ApartmentSearchBar />
              </Suspense>
            </div>
          )}

          {/* Desktop Navigation */}
          <div className={`hidden lg:flex lg:items-center lg:space-x-4 ${isLightHeader ? 'text-gray-800' : 'text-white'}`}>
            <Link href={englishGuideDestination(pathname ?? '/')} hrefLang="en" lang="en" className="inline-flex min-h-11 items-center px-3 text-sm font-semibold underline underline-offset-4">English guides</Link>
            {/* Apartamentos en la navegación global (auditoría 2026-09-29 D4): el hub
                no tenía ningún enlace desde cabecera, pie, home, fichas ni guías. */}
            <Link
              href="/apartamentos"
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold backdrop-blur-sm transition-all ${
                isLightHeader
                  ? 'border border-gray-300 text-gray-800 hover:border-primary-500 hover:text-primary-700'
                  : 'border border-white/70 text-white/90 hover:border-white hover:bg-white/15'
              }`}
            >
              Apartamentos
            </Link>
            <Link
              href="/tours"
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold backdrop-blur-sm transition-all ${
                isLightHeader
                  ? 'border border-gray-300 text-gray-800 hover:border-primary-500 hover:text-primary-700'
                  : 'border border-white/70 text-white/90 hover:border-white hover:bg-white/15'
              }`}
            >
              Tours
            </Link>
            {/* Botón WhatsApp Desktop — context-aware: con búsqueda activa va
                directo a wa.me calificado; si no, abre el asistente (variante
                tour en /tours). El cta_click_whatsapp lo emite el sheet al
                salir (o el bypass al click), no este wrapper. */}
            <SmartWhatsAppCTA
              placement="header"
              className={`inline-flex items-center gap-2 rounded-full bg-wsp-brand px-5 py-2.5 text-sm font-semibold text-wsp-ink hover:bg-wsp-brand-dark transition-all hover:scale-105 ${
                isLightHeader
                  ? 'shadow-md'
                  : 'drop-shadow-[0_3px_6px_rgba(255,255,255,0.35)]'
              }`}
            >
              <WhatsAppGlyph size={16} />
              WhatsApp
            </SmartWhatsAppCTA>
            {/* Login admin retirado del header público 2026-04-21 (Gemini v4):
                confunde huéspedes públicos. Acceso por /admin/login directo. */}
          </div>

          {/* Mobile Actions */}
          <div className="flex shrink-0 items-center gap-2 lg:hidden">
            <Link href={englishGuideDestination(pathname ?? '/')} hrefLang="en" lang="en" aria-label="Travel guides in English"
              className={`inline-flex min-h-11 items-center px-1 text-xs font-semibold underline underline-offset-4 ${isLightHeader ? 'text-gray-800' : 'text-white'}`}>EN</Link>
            <Link
              href="/tours"
              className={`inline-flex items-center rounded-full px-3 py-2 text-xs font-semibold backdrop-blur-sm transition-all min-h-[40px] ${
                isLightHeader
                  ? 'border border-gray-300 text-gray-800 hover:border-primary-500 hover:text-primary-700'
                  : 'border border-white/60 text-white/90 hover:border-white hover:bg-white/15'
              }`}
            >
              Tours
            </Link>
            {/* WhatsApp Button Mobile — context-aware (mismo resolutor que desktop) */}
            <SmartWhatsAppCTA
              placement="header"
              className={`p-2 bg-wsp-brand hover:bg-wsp-brand-dark rounded-full text-wsp-ink transition-transform active:scale-95 ${
                isLightHeader
                  ? 'shadow-md'
                  : 'drop-shadow-[0_3px_6px_rgba(255,255,255,0.35)]'
              }`}
              ariaLabel="Contactar por WhatsApp"
            >
              <WhatsAppGlyph size={16} />
            </SmartWhatsAppCTA>
          </div>
        </div>
      </nav>
    </header>
  );
}
